import { Component, Prop, Vue } from 'vue-property-decorator';
import ClIcon from '../ClIcon';
import ClDialogContent from '../ClDialogContent';
import ClIconButton from '../ClIconButton';
import ClModal from '../ClModal';
import ClPaper from '../ClPaper';
import ClToolbar from '../ClToolbar';
import ClTypography from '../ClTypography';
import { TransitionProps } from '../types';
import { applyClasses } from '../../lib';

type ScrollType = 'paper' | 'body';
type MaxWidthType = 'xs' | 'sm' | 'md' | 'lg' | false;
type Classes = {
  paper: any;
  modal: any;
  closeButton: any;
  title: any;
  backdrop: any;
};

@Component({
  components: {
    ClDialogContent,
    ClIcon,
    ClIconButton,
    ClModal,
    ClPaper,
    ClToolbar,
    ClTypography
  }
})
export default class ClDialog extends Vue {
  /**
   * Transition props for backdrop element
   */
  @Prop({
    type: Object as () => TransitionProps
  })
  public backdropTransitionProps!: TransitionProps;

  /**
   * Applies a animation-duration css value for backdrop element
   */
  @Prop(String)
  public backdropTransitionDuration!: string;

  /**
   * Class names for internal components
   */
  @Prop({
    type: Object as () => Classes,
    default: () => ({
      paper: '',
      modal: '',
      closeButton: '',
      title: '',
      backdrop: ''
    })
  })
  public classes!: Classes;

  /**
   * Shortcut to a paragraph of content.
   * This property can be overridden by the content slot.
   */
  @Prop({
    type: String,
    default: ''
  })
  public content!: string;

  /**
   * The props of the cl-modal component.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public clModalProps!: object;

  /**
   * The props of the cl-paper component.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public clPaperProps!: object;

  /**
   * If true, clicking the backdrop will not emit the close event.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disableBackdropClick!: boolean;

  /**
   * If `true`, the dialog will be rendered apart from the portal.
   * This is useful when your current component doesnt have a cl-provider.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disablePortal!: boolean;

  /**
   * If true, the dialog will be full-screen.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public fullScreen!: boolean;

  /**
   * If true, the dialog stretches to maxWidth.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public fullWidth!: boolean;

  /**
   * Determine the max width of the dialog. Set to false to disable maxWidth.
   */
  @Prop({
    type: [String, Boolean],
    default: 'sm'
  })
  public maxWidth!: MaxWidthType;

  /**
   * If true, the Dialog is open.
   */
  @Prop({
    type: Boolean,
    default: true
  })
  public open!: boolean;

  /**
   * Determine the container for scrolling the dialog.
   */
  @Prop({
    default: 'paper',
    type: String as () => ScrollType,
    validator: x => ['paper', 'body'].includes(x)
  })
  public scroll!: ScrollType;

  /**
   * If true, show the top-left close button.
   * You can use a custom close icon by utilizing the "title-right" slot.
   */
  @Prop({
    type: Boolean,
    default: true
  })
  public showCloseButton!: boolean;

  /**
   * Shortcut to a title. You can override this prop with the slot "title-left".
   */
  @Prop(String)
  public title!: string;

  /**
   * Transition props for dialog element
   */
  @Prop({
    type: Object as () => TransitionProps
  })
  public transitionProps!: TransitionProps;

  /**
   * animation-duration value for paper element
   */
  @Prop(String)
  public transitionDuration!: string;

  handleEntering(el: Element, done: () => void) {
    this.$emit('entering', el, done);
  }

  handleExiting(el: Element, done: () => void) {
    this.$emit('exiting', el, done);
  }

  get paperClassNames(): object {
    const paperPropsClass = (this.clPaperProps as any).class;
    return applyClasses(
      {
        'cl-dialog__paper': true,
        [`cl-dialog__paper--width-${this.maxWidth}`]: this.maxWidth !== false,
        [`cl-dialog__paper--scroll-${this.scroll}`]: true,
        [`cl-dialog__paper--full-width`]: this.fullWidth,
        [`cl-dialog__paper--full-screen`]: this.fullScreen,
        [paperPropsClass]: Boolean(paperPropsClass)
      },
      this.classes.paper
    );
  }

  get closeButtonClassName(): object {
    return applyClasses('cl-dialog__close-button', this.classes.closeButton);
  }

  get titleClassName(): object {
    return applyClasses('cl-dialog__title', this.classes.title);
  }
}
